import {Button, closeCurrentDialog, openDeleteDialog, openDialog} from 'platform/components';
import {Heading, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useParams} from 'react-router-dom';

import {mergeAll} from 'ramda';
import {isArray, noop} from 'ramda-adjunct';

import {
  useDeleteCustomerContractIndividualPriceLabourMutation,
  useDeleteCustomerContractIndividualPriceMaterialMutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DmsDataGrid, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import type {QueryFilterObject, ActionCallback} from 'features/eag-grid';

import {IndividualMaterialPriceForm} from './components/IndividualMaterialPriceForm';
import {IndividualWorkPriceForm} from './components/IndividualWorkPriceForm';

export function IndividualPricesTab() {
  const {id} = useParams();
  const [deleteMaterialPrice] = useDeleteCustomerContractIndividualPriceMaterialMutation();
  const [deleteLabourPrice] = useDeleteCustomerContractIndividualPriceLabourMutation();

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {customerContractId: id ?? ''}]);

  const handleAddWork = () =>
    openDialog(
      <IndividualWorkPriceForm
        customerContractId={id}
        onClose={closeCurrentDialog}
        afterSubmit={() => refreshDmsDatagrid('customer-contract-individual-price-labour')}
        data-testid={testIds.settings.customerContractsDetail('individualWorkPriceForm')}
      />,
      {
        title: i18n.t('entity.customerContract.labels.newIndividualPriceWork'),
        scrollBehavior: 'outside',
      }
    );

  const handleAddMaterial = () =>
    openDialog(
      <IndividualMaterialPriceForm
        customerContractId={id}
        onClose={closeCurrentDialog}
        afterSubmit={() => refreshDmsDatagrid('customer-contract-individual-price-material')}
        data-testid={testIds.settings.customerContractsDetail('individualMaterialPriceForm')}
      />,
      {
        title: i18n.t('entity.customerContract.labels.newIndividualPriceMaterial'),
        scrollBehavior: 'outside',
      }
    );

  const materialActionCallback: ActionCallback = ({actionKey, rowId}) => {
    const individualPriceMaterialId = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('edit', 'redirectDetail', () =>
        openDialog(
          <IndividualMaterialPriceForm
            customerContractId={id}
            individualPriceMaterialId={individualPriceMaterialId}
            onClose={closeCurrentDialog}
            afterSubmit={() => refreshDmsDatagrid('customer-contract-individual-price-material')}
          />,
          {
            title: i18n.t('entity.customerContract.labels.editIndividualPriceMaterial'),
            scrollBehavior: 'outside',
          }
        )
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteMaterialPrice({
              customerContractId: id ?? '',
              individualPriceMaterialId,
            })
              .unwrap()
              .then(() => refreshDmsDatagrid('customer-contract-individual-price-material'))
              .catch(handleApiError),
        })
      )
      .otherwise(noop);
  };

  const workActionCallback: ActionCallback = ({actionKey, rowId}) => {
    const individualPriceLabourId = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('edit', 'redirectDetail', () =>
        openDialog(
          <IndividualWorkPriceForm
            customerContractId={id}
            individualPriceLabourId={individualPriceLabourId}
            onClose={closeCurrentDialog}
            afterSubmit={() => refreshDmsDatagrid('customer-contract-individual-price-labour')}
          />,
          {
            title: i18n.t('entity.customerContract.labels.editIndividualPriceWork'),
            scrollBehavior: 'outside',
          }
        )
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteLabourPrice({
              customerContractId: id ?? '',
              individualPriceLabourId,
            })
              .unwrap()
              .then(() => refreshDmsDatagrid('customer-contract-individual-price-labour'))
              .catch(handleApiError),
        })
      )
      .otherwise(noop);
  };

  return (
    <VStack spacing={4}>
      <HStack justify="space-between" align="center">
        <Heading size={4}>{i18n.t('entity.order.labels.work')}</Heading>
        <Button
          title={i18n.t('general.actions.add')}
          variant="link"
          leftIcon="content/add_circle"
          size="small"
          onClick={handleAddWork}
          data-testid={testIds.settings.customerContractsDetail('addInvidualWork')}
        />
      </HStack>
      <DmsDataGrid
        gridCode="customer-contract-individual-price-labour"
        queryModifier={queryModifier}
        autoHeight
        actionCallback={workActionCallback}
        data-testid={testIds.settings.customerContractsDetail('individualPricesLabour')}
      />
      <HStack justify="space-between" align="center">
        <Heading size={4}>{i18n.t('entity.order.labels.material')}</Heading>
        <Button
          title={i18n.t('general.actions.add')}
          variant="link"
          leftIcon="content/add_circle"
          size="small"
          onClick={handleAddMaterial}
          data-testid={testIds.settings.customerContractsDetail('addInvidualMaterial')}
        />
      </HStack>
      <DmsDataGrid
        gridCode="customer-contract-individual-price-material"
        queryModifier={queryModifier}
        autoHeight
        actionCallback={materialActionCallback}
        data-testid={testIds.settings.customerContractsDetail('individualPricesMaterial')}
      />
    </VStack>
  );
}
